<script setup lang="ts">
import { nextTick, onBeforeUnmount, ref, useId, useTemplateRef } from 'vue'

/**
 * A chip's trigger and the small non-modal panel it opens. Closes on Escape, on a click
 * outside, and when focus leaves - except into a teleported listbox a field inside the panel
 * owns, which is still part of the panel as far as the user is concerned.
 */
defineProps<{ label: string }>()

const open = ref(false)
const panelId = useId()
const root = useTemplateRef<HTMLElement>('root')
const trigger = useTemplateRef<HTMLButtonElement>('trigger')
const panel = useTemplateRef<HTMLElement>('panel')

function isInside(target: EventTarget | null): boolean {
  if (!(target instanceof Node)) return false
  if (root.value?.contains(target)) return true
  return target instanceof Element && target.closest('[role="listbox"]') !== null
}

function onDocumentPointer(event: PointerEvent): void {
  if (!isInside(event.target)) close(false)
}

function show(): void {
  open.value = true
  document.addEventListener('pointerdown', onDocumentPointer)
  void nextTick(() => panel.value?.querySelector<HTMLElement>('input, select, button')?.focus())
}

function close(returnFocus: boolean): void {
  if (!open.value) return
  open.value = false
  document.removeEventListener('pointerdown', onDocumentPointer)
  if (returnFocus) trigger.value?.focus()
}

function onKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape' && open.value && !event.defaultPrevented) {
    event.preventDefault()
    close(true)
  }
}

function onFocusout(event: FocusEvent): void {
  if (event.relatedTarget !== null && !isInside(event.relatedTarget)) close(false)
}

onBeforeUnmount(() => document.removeEventListener('pointerdown', onDocumentPointer))
</script>

<template>
  <div ref="root" class="flex min-w-0 items-stretch" @keydown="onKeydown" @focusout="onFocusout">
    <button
      ref="trigger"
      type="button"
      :aria-label="label"
      :aria-expanded="open"
      :aria-controls="open ? panelId : undefined"
      aria-haspopup="dialog"
      class="flex min-w-0 items-center gap-[var(--space-tight)] rounded-[var(--radius-control)] pl-[var(--space-cell)] pr-[var(--space-inline)] text-[length:var(--text-step-2)]"
      @click="open ? close(false) : show()"
    >
      <slot name="trigger" />
      <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true" class="shrink-0">
        <path d="M4 6.5 8 10.5 12 6.5" fill="none" stroke="currentColor" stroke-width="1.6" />
      </svg>
    </button>
    <div
      v-if="open"
      :id="panelId"
      ref="panel"
      role="dialog"
      :aria-label="label"
      tabindex="-1"
      class="absolute left-0 top-full z-30 mt-[var(--space-tight)] flex w-max min-w-64 flex-col gap-[var(--space-cell)] rounded-[var(--radius-overlay)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)] p-[var(--space-cell)] font-normal text-[color:var(--color-text)] shadow-[var(--shadow-overlay)] outline-none"
    >
      <slot :close="() => close(true)" />
    </div>
  </div>
</template>
